import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { useAuthStore } from '@/features/auth/store/authStore'

import { storeApi } from '../api/storeApi'
import type { EventSeriesPayload, StoreEventPayload } from '../types/event.types'
import type {
  PublicVenue,
  StoreAccount,
  StoreAccountPayload,
  StoreCampaignPayload,
  StoreCampaignResponse,
  StoreVenue,
  StoreVenuePayload,
  StoreVenueZonePayload,
  VenueClosurePayload,
} from '../types/store.types'

export const storeKeys = {
  account: ['store', 'account'] as const,
  venues: ['store', 'venues'] as const,
  venue: (id: number) => ['store', 'venues', id] as const,
  venueCampaigns: (venueId: number) => ['store', 'venues', venueId, 'campaigns'] as const,
  venueZones: (venueId: number) => ['store', 'venues', venueId, 'zones'] as const,
  venueClosures: (venueId: number) => ['store', 'venues', venueId, 'closures'] as const,
  directory: (provinceCode?: string, cityCode?: string) =>
    ['stores', 'directory', provinceCode ?? null, cityCode ?? null] as const,
  public: (slug: string) => ['stores', 'public', slug] as const,
  publicSessions: (slug: string) => ['stores', 'public', slug, 'sessions'] as const,
  zoneAvailability: (zoneId: number, date: string) =>
    ['stores', 'zone-availability', zoneId, date] as const,
  agenda: ['store', 'agenda'] as const,
  stats: ['store', 'stats'] as const,
  vips: ['store', 'vips'] as const,
  vipsList: (page: number, q: string) => ['store', 'vips', 'list', page, q] as const,
  vipSearch: (q: string) => ['store', 'vips', 'search', q] as const,
  myVipMemberships: ['me', 'vips'] as const,
  events: ['store', 'events'] as const,
  eventSeries: ['store', 'event-series'] as const,
  seriesOccurrences: (id: number) => ['store', 'event-series', id, 'occurrences'] as const,
  followedVenues: ['me', 'followed-venues'] as const,
}

/** Gate común: solo tiene sentido pedir datos propios de tienda si el user
 *  autenticado ya tiene el rol TIENDA (mismo gotcha que cualquier query
 *  `/me/*` — evita el 401 → interceptor → rebote a /login de un anónimo).
 *  Exportado para que otros módulos de tienda (p.ej. mensajería) compartan
 *  el mismo gate sin duplicarlo. */
export function useIsStoreOwner(): boolean {
  const { isAuthenticated, user } = useAuth()
  return isAuthenticated && user?.role === 'TIENDA'
}

// ---------------------------------------------------------------------------
// Nivel 1 — Cuenta
// ---------------------------------------------------------------------------

/**
 * Cuenta propia de la tienda. Gated a `role === 'TIENDA'` — un USER normal
 * aún no tiene cuenta (el registro es lo que concede el rol) y un anónimo ni
 * siquiera debería intentar el GET.
 */
export function useMyAccountQuery() {
  const enabled = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.account,
    queryFn: () => storeApi.getMyAccount(),
    enabled,
  })
}

/**
 * Alta "Soy una tienda" (`POST /store/register`). Al tener éxito:
 * - Sembramos el cache de `useMyAccountQuery` con la respuesta autoritativa.
 * - Sincronizamos `authStore.currentUser.role` a `TIENDA` en el momento: el
 *   backend ya concedió el rol, pero el JWT/`authStore` en memoria del FE es
 *   un snapshot del login y no se refresca solo.
 */
export function useRegisterStoreAccountMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StoreAccountPayload) => storeApi.registerAccount(payload),
    onSuccess: (account: StoreAccount) => {
      qc.setQueryData(storeKeys.account, account)
      const current = useAuthStore.getState().currentUser
      if (current && current.role !== 'TIENDA') {
        useAuthStore.getState().setCurrentUser({ ...current, role: 'TIENDA' })
      }
    },
  })
}

/** PUT /store/me — edita la cuenta. Siembra el cache con la respuesta
 *  autoritativa (nos ahorramos un segundo GET). */
export function useUpdateMyAccountMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StoreAccountPayload) => storeApi.updateMyAccount(payload),
    onSuccess: (account: StoreAccount) => qc.setQueryData(storeKeys.account, account),
  })
}

/** DELETE /store/me — da de baja la cuenta de tienda. El caller debe hacer
 *  logout en onSuccess (el token TIENDA queda obsoleto). */
export function useCloseMyAccountMutation() {
  return useMutation({
    mutationFn: () => storeApi.closeMyAccount(),
  })
}

/** POST /store/me/logo — el backend devuelve la cuenta completa ya
 *  actualizada, así que sembramos el cache igual que updateMyAccount. */
export function useUploadMyLogoMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => storeApi.uploadLogo(file),
    onSuccess: (account: StoreAccount) => qc.setQueryData(storeKeys.account, account),
  })
}

// ---------------------------------------------------------------------------
// Nivel 2 — Locales
// ---------------------------------------------------------------------------

/** Locales propios (con su `venueStatus`). Mismo gate que la cuenta. */
export function useMyVenuesQuery() {
  const enabled = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.venues,
    queryFn: () => storeApi.listVenues(),
    enabled,
  })
}

/** Ficha de un local propio. */
export function useMyVenueQuery(venueId: number | undefined) {
  const owner = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.venue(venueId ?? 0),
    queryFn: () => storeApi.getVenue(venueId!),
    enabled: owner && Boolean(venueId),
  })
}

function useInvalidateVenues() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: storeKeys.venues })
}

/** POST /store/me/venues — exige cuenta APPROVED (el backend responde 403
 *  `error.store.access.denied` si no). */
export function useCreateVenueMutation() {
  const invalidate = useInvalidateVenues()
  return useMutation({
    mutationFn: (payload: StoreVenuePayload) => storeApi.createVenue(payload),
    onSuccess: invalidate,
  })
}

export function useUpdateVenueMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: StoreVenuePayload }) =>
      storeApi.updateVenue(id, payload),
    onSuccess: (venue: StoreVenue) => {
      qc.setQueryData(storeKeys.venue(venue.id), venue)
      void qc.invalidateQueries({ queryKey: storeKeys.venues })
    },
  })
}

export function useDeleteVenueMutation() {
  const invalidate = useInvalidateVenues()
  return useMutation({
    mutationFn: (id: number) => storeApi.deleteVenue(id),
    onSuccess: invalidate,
  })
}

/** Cartelera con el local al que pertenece — para la vista resumen del
 *  dashboard (`StoreHomePage`), que lista carteleras de TODOS los locales. */
export interface VenueCampaignWithVenue extends StoreCampaignResponse {
  venueId: number
  venueName: string
}

/**
 * Carteleras de TODOS los locales propios (para los KPIs/listado del
 * dashboard `/tienda`). No hay endpoint agregado en el backend — cada local
 * expone sus carteleras por separado (`GET .../venues/{id}/campaigns`) — así
 * que hacemos una petición por local vía `useQueries` (el número de locales
 * por tienda es pequeño, no hay paginación que perder).
 */
export function useMyAllVenueCampaignsQuery(venues: StoreVenue[] | undefined) {
  const owner = useIsStoreOwner()
  const list = venues ?? []
  const results = useQueries({
    queries: list.map((v) => ({
      queryKey: storeKeys.venueCampaigns(v.id),
      queryFn: () => storeApi.listVenueCampaigns(v.id),
      enabled: owner,
    })),
  })

  const isLoading = owner && results.some((r) => r.isLoading)
  const isError = results.some((r) => r.isError)
  const data: VenueCampaignWithVenue[] = results.flatMap((r, i) => {
    const venue = list[i]!
    return (r.data ?? []).map((c) => ({ ...c, venueId: venue.id, venueName: venue.name }))
  })

  return { data, isLoading, isError }
}

// ---------------------------------------------------------------------------
// Nivel 3 — Carteleras de un local
// ---------------------------------------------------------------------------

/** Carteleras propias de un local (con su `moderationStatus`). */
export function useVenueCampaignsQuery(venueId: number | undefined) {
  const owner = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.venueCampaigns(venueId ?? 0),
    queryFn: () => storeApi.listVenueCampaigns(venueId!),
    enabled: owner && Boolean(venueId),
  })
}

function useInvalidateVenueCampaigns(venueId: number) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: storeKeys.venueCampaigns(venueId) })
}

/** POST /store/me/venues/{id}/campaigns — exige local APPROVED (403
 *  `error.venue.access.denied` si no). */
export function useCreateVenueCampaignMutation(venueId: number) {
  const invalidate = useInvalidateVenueCampaigns(venueId)
  return useMutation({
    mutationFn: (payload: StoreCampaignPayload) => storeApi.createVenueCampaign(venueId, payload),
    onSuccess: invalidate,
  })
}

export function useUpdateVenueCampaignMutation(venueId: number) {
  const invalidate = useInvalidateVenueCampaigns(venueId)
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: StoreCampaignPayload }) =>
      storeApi.updateVenueCampaign(venueId, id, payload),
    onSuccess: invalidate,
  })
}

export function useDeleteVenueCampaignMutation(venueId: number) {
  const invalidate = useInvalidateVenueCampaigns(venueId)
  return useMutation({
    mutationFn: (campaignId: number) => storeApi.deleteVenueCampaign(venueId, campaignId),
    onSuccess: invalidate,
  })
}

// ---------------------------------------------------------------------------
// Nivel 3 — Zonas de juego de un local
// ---------------------------------------------------------------------------

/** Zonas de juego propias de un local (mismo gate que las carteleras). */
export function useVenueZonesQuery(venueId: number | undefined) {
  const owner = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.venueZones(venueId ?? 0),
    queryFn: () => storeApi.listVenueZones(venueId!),
    enabled: owner && Boolean(venueId),
  })
}

function useInvalidateVenueZones(venueId: number) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: storeKeys.venueZones(venueId) })
}

/** POST /store/me/venues/{id}/zones — tipo duplicado → 409. */
export function useCreateVenueZoneMutation(venueId: number) {
  const invalidate = useInvalidateVenueZones(venueId)
  return useMutation({
    mutationFn: (payload: StoreVenueZonePayload) => storeApi.createVenueZone(venueId, payload),
    onSuccess: invalidate,
  })
}

export function useUpdateVenueZoneMutation(venueId: number) {
  const invalidate = useInvalidateVenueZones(venueId)
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: StoreVenueZonePayload }) =>
      storeApi.updateVenueZone(venueId, id, payload),
    onSuccess: invalidate,
  })
}

export function useDeleteVenueZoneMutation(venueId: number) {
  const invalidate = useInvalidateVenueZones(venueId)
  return useMutation({
    mutationFn: (zoneId: number) => storeApi.deleteVenueZone(venueId, zoneId),
    onSuccess: invalidate,
  })
}

/** Días de cierre / vacaciones propios de un local (mismo gate que zonas). */
export function useVenueClosuresQuery(venueId: number | undefined) {
  const owner = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.venueClosures(venueId ?? 0),
    queryFn: () => storeApi.listVenueClosures(venueId!),
    enabled: owner && Boolean(venueId),
  })
}

function useInvalidateVenueClosures(venueId: number) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: storeKeys.venueClosures(venueId) })
}

export function useCreateVenueClosureMutation(venueId: number) {
  const invalidate = useInvalidateVenueClosures(venueId)
  return useMutation({
    mutationFn: (payload: VenueClosurePayload) => storeApi.createVenueClosure(venueId, payload),
    onSuccess: invalidate,
  })
}

export function useDeleteVenueClosureMutation(venueId: number) {
  const invalidate = useInvalidateVenueClosures(venueId)
  return useMutation({
    mutationFn: (closureId: number) => storeApi.deleteVenueClosure(venueId, closureId),
    onSuccess: invalidate,
  })
}

// ---------------------------------------------------------------------------
// Agenda del owner (partidas de sus locales)
// ---------------------------------------------------------------------------

/** GET /store/me/agenda — próximas partidas de los locales del owner. Mismo
 *  gate que el resto de datos propios de tienda. */
export function useStoreAgendaQuery() {
  const enabled = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.agenda,
    queryFn: () => storeApi.getMyAgenda(),
    enabled,
  })
}

/** GET /store/me/stats — estadísticas de ocupación de los locales del owner. */
export function useStoreStatsQuery() {
  const enabled = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.stats,
    queryFn: () => storeApi.getMyStats(),
    enabled,
  })
}

/** DELETE /store/me/venues/{venueId}/agenda/{sessionId} — quita la partida del
 *  tablero del local (libera la mesa + desvincula + bloquea el re-vínculo).
 *  Invalida la agenda para que la fila desaparezca. */
export function useRemoveFromAgendaMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ venueId, sessionId }: { venueId: number; sessionId: number }) =>
      storeApi.removeFromAgenda(venueId, sessionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: storeKeys.agenda })
      // Quitar libera la mesa → refresca disponibilidad pública (ficha/directorio).
      void qc.invalidateQueries({ queryKey: ['stores'] })
    },
  })
}

// ---------------------------------------------------------------------------
// VIP de tienda (ver docs/specs/2026-08-23-vip-tienda.md)
// ---------------------------------------------------------------------------

/** GET /store/me/vips — lista paginada (5/pág) de VIP de la tienda, filtrable por
 *  nick o email. `placeholderData` mantiene la página previa al cambiar para evitar
 *  parpadeo. */
export function useStoreVipsQuery(page: number, q: string) {
  const enabled = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.vipsList(page, q),
    queryFn: () => storeApi.listVips(page, q),
    enabled,
    placeholderData: keepPreviousData,
  })
}

/** GET /store/me/vips/search?q= — typeahead de usuarios por nick. Se dispara
 *  solo con ≥2 caracteres (evita ruido y golpes al back por cada tecla). */
export function useVipUserSearchQuery(q: string) {
  const owner = useIsStoreOwner()
  const term = q.trim()
  return useQuery({
    queryKey: storeKeys.vipSearch(term),
    queryFn: () => storeApi.searchVipUsers(term),
    enabled: owner && term.length >= 2,
    staleTime: 30_000,
  })
}

function useInvalidateVips() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: storeKeys.vips })
}

/** POST /store/me/vips — añade (o re-fecha) un VIP. */
export function useAddVipMutation() {
  const invalidate = useInvalidateVips()
  return useMutation({
    mutationFn: (payload: { userId: number; expiresAt: string | null }) => storeApi.addVip(payload),
    onSuccess: invalidate,
  })
}

/** PATCH /store/me/vips/{id} — cambia la caducidad (null = permanente). */
export function useUpdateVipExpiryMutation() {
  const invalidate = useInvalidateVips()
  return useMutation({
    mutationFn: ({ id, expiresAt }: { id: number; expiresAt: string | null }) =>
      storeApi.updateVipExpiry(id, expiresAt),
    onSuccess: invalidate,
  })
}

/** POST /store/me/vips/{id}/extend — +1 mes. */
export function useExtendVipMutation() {
  const invalidate = useInvalidateVips()
  return useMutation({
    mutationFn: (id: number) => storeApi.extendVip(id),
    onSuccess: invalidate,
  })
}

/** DELETE /store/me/vips/{id} — quita el VIP. */
export function useRemoveVipMutation() {
  const invalidate = useInvalidateVips()
  return useMutation({
    mutationFn: (id: number) => storeApi.removeVip(id),
    onSuccess: invalidate,
  })
}

/** GET /me/vips — tiendas donde el usuario actual es VIP activo (insignia de
 *  perfil). Gated a auth (query `/me/*`): un anónimo no debe dispararla. */
export function useMyVipMembershipsQuery() {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: storeKeys.myVipMemberships,
    queryFn: () => storeApi.getMyVipMemberships(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  })
}

// ---------------------------------------------------------------------------
// Eventos de tienda (torneos/eventos) + series recurrentes
// ---------------------------------------------------------------------------

/** GET /store/me/events — eventos del owner (kind != PLAYER_GAME). */
export function useStoreEventsQuery() {
  const enabled = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.events,
    queryFn: () => storeApi.listEvents(),
    enabled,
  })
}

/** GET /store/me/event-series — series recurrentes del owner. */
export function useEventSeriesQuery() {
  const enabled = useIsStoreOwner()
  return useQuery({
    queryKey: storeKeys.eventSeries,
    queryFn: () => storeApi.listEventSeries(),
    enabled,
  })
}

/** POST /store/me/events — crea un evento suelto. Refresca la lista propia y el
 *  feed público (`['sessions']`), donde el evento ya aparece con su badge. */
export function useCreateStoreEventMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StoreEventPayload) => storeApi.createEvent(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: storeKeys.events })
      void qc.invalidateQueries({ queryKey: ['sessions'] })
      // El evento ocupa mesa → refresca disponibilidad pública (ficha/directorio).
      void qc.invalidateQueries({ queryKey: ['stores'] })
    },
  })
}

/** POST /store/me/event-series — crea una serie recurrente. */
export function useCreateEventSeriesMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: EventSeriesPayload) => storeApi.createEventSeries(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.eventSeries }),
  })
}

/** PUT /store/me/event-series/{id} — edita la plantilla de una serie. */
export function useUpdateEventSeriesMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: EventSeriesPayload }) =>
      storeApi.updateEventSeries(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: storeKeys.eventSeries })
      void qc.invalidateQueries({ queryKey: ['sessions'] })
    },
  })
}

/** DELETE /store/me/event-series/{id} — desactiva la serie. */
export function useDeactivateEventSeriesMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => storeApi.deactivateEventSeries(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.eventSeries }),
  })
}

/** GET /store/me/event-series/{id}/occurrences — ocurrencias futuras de una serie.
 *  Se dispara solo al expandirla (`enabled`). */
export function useSeriesOccurrencesQuery(id: number, enabled: boolean) {
  return useQuery({
    queryKey: storeKeys.seriesOccurrences(id),
    queryFn: () => storeApi.listSeriesOccurrences(id),
    enabled,
    staleTime: 30_000,
  })
}

/** DELETE /store/me/event-series/{id}/permanent — borra una serie inactiva. */
export function useDeleteEventSeriesPermanentMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => storeApi.deleteEventSeriesPermanent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.eventSeries }),
  })
}

// ---------------------------------------------------------------------------
// Público (directorio + ficha de local)
// ---------------------------------------------------------------------------

/** GET /venues — directorio público de locales por zona. Sin gate de auth:
 *  visible a anónimos igual que `/sessions`. */
export function useStoreDirectoryQuery(provinceCode?: string, cityCode?: string) {
  return useQuery({
    queryKey: storeKeys.directory(provinceCode, cityCode),
    queryFn: () => storeApi.directory(provinceCode, cityCode),
    staleTime: 60_000,
  })
}

/** GET /venues/{slug} — ficha pública de un local. 404 si no existe o no
 *  está APPROVED — el caller distingue por `error.status` (ver `ApiError`). */
export function usePublicStoreQuery(slug: string | undefined) {
  return useQuery({
    queryKey: storeKeys.public(slug ?? ''),
    queryFn: () => storeApi.getPublic(slug!),
    enabled: Boolean(slug),
    staleTime: 60_000,
  })
}

/** GET /venues/{slug}/sessions — próximas partidas del local, para el bloque
 *  "próximas partidas aquí" de la ficha pública. Sin gate de auth (público
 *  igual que la ficha). */
export function usePublicStoreSessionsQuery(slug: string | undefined) {
  return useQuery({
    queryKey: storeKeys.publicSessions(slug ?? ''),
    queryFn: () => storeApi.getPublicUpcomingSessions(slug!),
    enabled: Boolean(slug),
    staleTime: 60_000,
  })
}

/** GET /venues/zones/{zoneId}/availability?date= — mesas libres de una zona en
 *  una fecha. La ocupación es por día, así que necesita zona + fecha. La consume
 *  el paso «Cuándo» de crear partida (solo cuando ambos están definidos). */
export function useZoneAvailabilityQuery(
  zoneId: number | null | undefined,
  date: string | null | undefined,
) {
  return useQuery({
    queryKey: storeKeys.zoneAvailability(zoneId ?? 0, date ?? ''),
    queryFn: () => storeApi.getZoneAvailability(zoneId!, date!),
    enabled: Boolean(zoneId) && Boolean(date),
    staleTime: 30_000,
  })
}

// ---------------------------------------------------------------------------
// Seguir a una tienda (spec 2026-11-02-seguir-tienda)
// ---------------------------------------------------------------------------

/** GET /me/followed-venues — «Locales que sigues» del perfil (solo con sesión). */
export function useFollowedVenuesQuery(enabled: boolean) {
  return useQuery({
    queryKey: storeKeys.followedVenues,
    queryFn: () => storeApi.listFollowedVenues(),
    enabled,
  })
}

/**
 * Seguir / dejar de seguir. Al responder, la ficha pública cacheada (`slug`, si la hay) pasa a
 * pintar el estado y el recuento que devuelve el servidor, y la lista del perfil se recarga.
 */
export function useFollowVenueMutation(slug?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ venueId, follow }: { venueId: number; follow: boolean }) =>
      follow ? storeApi.followVenue(venueId) : storeApi.unfollowVenue(venueId),
    onSuccess: (status) => {
      if (slug) {
        qc.setQueryData<PublicVenue>(storeKeys.public(slug), (prev) =>
          prev
            ? { ...prev, followedByMe: status.following, followersCount: status.followersCount }
            : prev,
        )
      }
      void qc.invalidateQueries({ queryKey: storeKeys.followedVenues })
    },
  })
}
